import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { useAppVersionChecker } from '@/hooks/use-app-version';

const UPDATE_TOAST_ID = 'app-version-update';

/**
 * 新しいバージョンを検知したら、次のページ遷移のタイミングでフルリロードする。
 *
 * 検知した瞬間にリロードを促すと入力途中の内容が失われるため、どのみち画面の状態が
 * 破棄されるページ遷移まで待ってから実行する。遷移時であれば確認を取る必要がないので
 * モーダルは出さず、そのまま最新のバンドルを読み直す。
 *
 * クエリパラメータだけの変化（ページャー・絞り込み・検索キーワード）は入力中にも発生するため、
 * pathname が変わったときだけリロード対象とする。
 *
 * 併せて、操作を妨げないトーストで更新があることを通知する。同じ画面に長時間留まると
 * 遷移が発生せず古いバンドルのまま動き続けるため、気づいた時点で更新できる導線を用意する。
 * 閉じられても次の遷移で自動更新されるので、あくまで近道と告知の位置づけ。
 *
 * BrowserRouter 配下でのみ利用できる。
 */
export function AppVersionGate() {
  const { enabled, hasUpdate, reload } = useAppVersionChecker();
  const { pathname } = useLocation();
  const lastPathnameRef = useRef(pathname);

  useEffect(() => {
    // 遷移以外の再実行（バージョン検知時など）では何もしない
    if (lastPathnameRef.current === pathname) return;
    lastPathnameRef.current = pathname;

    if (enabled && hasUpdate) reload();
  }, [pathname, enabled, hasUpdate, reload]);

  useEffect(() => {
    if (!enabled || !hasUpdate) return;

    // 同じ toastId の呼び出しは無視されるため、ポーリングや再描画で多重表示されない。
    // 席を外していても気づけるよう自動で閉じず、誤操作で消えないようクリック・ドラッグも無効にする
    toast.info(
      <div className="flex flex-col items-start gap-2">
        <span>新しいバージョンがあります</span>
        <Button variant="outlined" className="px-3 text-xs" onClick={reload}>
          今すぐ更新
        </Button>
      </div>,
      {
        toastId: UPDATE_TOAST_ID,
        position: 'top-center',
        autoClose: false,
        closeOnClick: false,
        draggable: false,
      },
    );
  }, [enabled, hasUpdate, reload]);

  return null;
}
